import {
  ITimeOffPolicy,
  ITimeOffPolicyData,
  ITimeOffPolicyFieldError,
  ITimeOffPolicyFieldErrorData,
  ITimeOffTemplate,
  ITimeOffTemplateData,
  ITimeOffTemplateFieldError,
  ITimeOffTemplateFieldErrorData,
  ITimeOffType,
  ITimeOffTypeData,
  ITimeOffTypeFieldError,
  ITimeOffTypeFieldErrorData,
  ITimeOffUnit,
  ITimeOffUnitData,
  TimeOffDurationType,
  TimeOffUnitEnum
} from './timeoff.types';

export class TimeOffTemplate implements ITimeOffTemplate {
  readonly id?: number;
  readonly name: string = '';
  readonly timeoffs?: ITimeOffType[] | null;

  constructor(data?: ITimeOffTemplateData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }
      if (data.name) {
        this.name = data.name;
      }

      if (data.timeoffs) {
        this.timeoffs = data.timeoffs.map(timeoff => new TimeOffType(timeoff));
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class TimeOffUnit implements ITimeOffUnit {
  readonly value?: TimeOffUnitEnum;
  readonly display_unit?: string;

  constructor(data?: ITimeOffUnitData) {
    if (data) {
      this.value = data.value ?? 0;

      if (data.display_unit) {
        this.display_unit = data.display_unit;
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class TimeOffType implements ITimeOffType {
  readonly id?: number;
  readonly name?: string = '';
  readonly code?: number | null = null;
  readonly policy?: ITimeOffPolicy | null = null;

  constructor(data?: ITimeOffTypeData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.name) {
        this.name = data.name;
      }

      if (data.code) {
        this.code = data.code;
      }

      if (data.policy) {
        this.policy = new TimeOffPolicy(data.policy);
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  //TODO: MG- 2022-12-08 : Look into why we need to use a getter for v-selects when selecting multiple chips
  get fullName(): string {
    let stringRepresentation = this.name ?? '';
    if (this.code) {
      stringRepresentation += ` (${this.code})`;
    }

    return stringRepresentation.trim();
  }

  toString(): string {
    let stringRepresentation = this.name ?? '';
    if (this.code) {
      stringRepresentation += ` (${this.code})`;
    }

    return stringRepresentation.trim();
  }
}

export class TimeOffPolicy implements ITimeOffPolicy {
  id?: number;
  name?: string = '';
  effective_date?: string | Date | null = null;
  expiry_date?: string | Date | null = null;
  is_accrual_supported?: boolean;
  is_entitlement_earned?: boolean;
  is_carryover_supported?: boolean;
  is_overdraft_supported = false;
  is_reminder_supported = false;
  timeoff_display_option?: number;
  yearly_entitlement?: number = 0;
  timeoff_unit?: ITimeOffUnit | number = new TimeOffUnit();
  timeoff?: number | ITimeOffType;
  timeoff_duration_type?: TimeOffDurationType;
  entitlement_amount_validity?: number;

  constructor(data?: ITimeOffPolicyData) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      if (data.name) {
        this.name = data.name;
      }

      if (data.effective_date) {
        this.effective_date = data.effective_date;
      }

      if (data.expiry_date) {
        this.expiry_date = data.expiry_date;
      }

      if (data.timeoff_unit) {
        if (typeof data.timeoff_unit == 'number') {
          this.timeoff_unit = data.timeoff_unit;
        } else {
          this.timeoff_unit = new TimeOffUnit(data.timeoff_unit);
        }
      }

      this.yearly_entitlement = data.yearly_entitlement ?? 0;

      this.is_accrual_supported = data.is_accrual_supported ?? false;

      this.is_entitlement_earned = data.is_entitlement_earned ?? false;

      this.is_carryover_supported = data.is_carryover_supported ?? false;

      this.is_overdraft_supported = data.is_overdraft_supported ?? false;

      if (data.is_reminder_supported) {
        this.is_reminder_supported = data.is_reminder_supported;
      }

      this.timeoff_display_option = data.timeoff_display_option ?? 0;

      this.timeoff_duration_type =
        data.timeoff_duration_type ?? TimeOffDurationType.Static;

      this.entitlement_amount_validity = data.entitlement_amount_validity ?? 0;

      if (data.timeoff) {
        if (typeof data.timeoff == 'number') {
          this.timeoff = data.timeoff;
        } else {
          this.timeoff = new TimeOffType(data.timeoff);
        }
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class TimeOffTemplateFieldError implements ITimeOffTemplateFieldError {
  readonly name: string[] = [];
  readonly timeoffs: ITimeOffTypeFieldError = new TimeOffTypeFieldError();

  constructor(data?: ITimeOffTemplateFieldErrorData) {
    if (data) {
      if (data.name) {
        this.name = data.name;
      }
      if (data.timeoffs) {
        this.timeoffs = new TimeOffTypeFieldError(data.timeoffs);
      }
    }
  }

  clearErrors(): void {
    this.name.splice(0, this.name.length);
    this.timeoffs.clearErrors();
  }

  static isTimeOffTemplateFieldError(
    data: ITimeOffTemplateFieldErrorData
  ): boolean {
    if (data.name && Array.isArray(data.name)) {
      return true;
    } else if (
      data.timeoffs &&
      TimeOffTypeFieldError.isTimeOffTypeFieldError(data.timeoffs)
    ) {
      return true;
    }
    return false;
  }
}

export class TimeOffTypeFieldError implements ITimeOffTypeFieldError {
  readonly name: string[] = [];
  readonly policy: ITimeOffPolicyFieldError = new TimeOffPolicyFieldError();

  constructor(data?: ITimeOffTypeFieldErrorData) {
    if (data) {
      if (data.name) {
        this.name = data.name;
      }
      if (data.policy) {
        this.policy = new TimeOffPolicyFieldError(data.policy);
      }
    }
  }

  clearErrors(): void {
    this.name.splice(0, this.name.length);
    this.policy.clearErrors();
  }

  static isTimeOffTypeFieldError(data: ITimeOffTypeFieldErrorData): boolean {
    if (data.name && Array.isArray(data.name)) {
      return true;
    }
    return false;
  }
}

export class TimeOffPolicyFieldError implements ITimeOffPolicyFieldError {
  readonly effective_date: string[] = [];
  readonly expiry_date: string[] = [];
  readonly yearly_entitlement: string[] = [];
  readonly timeoff: string[] = [];
  readonly is_carryover_supported: string[] = [];
  readonly entitlement_amount_validity: string[] = [];

  constructor(data?: ITimeOffPolicyFieldErrorData) {
    if (data) {
      if (data.effective_date) {
        this.effective_date = data.effective_date;
      }
      if (data.expiry_date) {
        this.expiry_date = data.expiry_date;
      }

      if (data.yearly_entitlement) {
        this.yearly_entitlement = data.yearly_entitlement;
      }

      if (data.timeoff) {
        this.timeoff = data.timeoff;
      }

      if (data.is_carryover_supported) {
        this.is_carryover_supported = data.is_carryover_supported;
      }

      if (data.entitlement_amount_validity) {
        this.entitlement_amount_validity = data.entitlement_amount_validity;
      }
    }
  }

  clearErrors(): void {
    this.effective_date.splice(0, this.effective_date.length);
    this.expiry_date.splice(0, this.expiry_date.length);
    this.yearly_entitlement.splice(0, this.yearly_entitlement.length);
    this.timeoff.splice(0, this.timeoff.length);
    this.is_carryover_supported.splice(0, this.is_carryover_supported.length);
    this.entitlement_amount_validity.splice(
      0,
      this.entitlement_amount_validity.length
    );
  }

  static isTimeOffPolicyFieldError(
    data: ITimeOffPolicyFieldErrorData
  ): boolean {
    if (data.effective_date && Array.isArray(data.effective_date)) {
      return true;
    }
    if (data.expiry_date && Array.isArray(data.expiry_date)) {
      return true;
    }
    if (data.yearly_entitlement && Array.isArray(data.yearly_entitlement)) {
      return true;
    }
    if (data.timeoff && Array.isArray(data.timeoff)) {
      return true;
    }
    if (
      data.is_carryover_supported &&
      Array.isArray(data.is_carryover_supported)
    ) {
      return true;
    }
    if (
      data.entitlement_amount_validity &&
      Array.isArray(data.entitlement_amount_validity)
    ) {
      return true;
    }
    return false;
  }
}
